/**
 * Estado dos filtros do modal SeletorUsinasEolicas (WINDX-021).
 *
 * Usa `useDeferredValue` (React 19 nativo) na busca textual — em vez de
 * `useState` + debounce manual. A digitacao reflete imediato no input,
 * mas a propagacao para o array filtrado e adiada quando o React esta
 * sob pressao. Substitui `use-debounce` sem adicionar dep.
 *
 * As listas multi-select (UF, subsistema, conjunto) sao arrays — quando
 * vazias, o filtro nao se aplica.
 *
 * Decisao: range de potencia armazenado como `min/max` separados em vez
 * de `[min, max]` tuple para casar com a API do react-slider (que ja e
 * usada em outros lugares no projeto) e para facilitar "limpar so o min".
 */
import {
  Dispatch, SetStateAction, useDeferredValue, useMemo, useState,
} from 'react';
import { FiltrosUsinas } from 'helper/windx';

export interface UseFiltrosUsinasReturn {
  filtros: FiltrosUsinas;
  // `busca` debounced — use no helper de filtragem, NAO no input
  filtrosEfetivos: FiltrosUsinas;
  setBusca: Dispatch<SetStateAction<string>>;
  setUfs: Dispatch<SetStateAction<string[]>>;
  setSubsistemasSm: Dispatch<SetStateAction<string[]>>;
  setConjuntos: Dispatch<SetStateAction<string[]>>;
  setPotenciaMin: Dispatch<SetStateAction<number | null>>;
  setPotenciaMax: Dispatch<SetStateAction<number | null>>;
  limparFiltros: () => void;
  temFiltrosAtivos: boolean;
}

const FILTROS_VAZIOS: FiltrosUsinas = {
  busca: '',
  ufs: [],
  subsistemasSm: [],
  conjuntos: [],
  potenciaMin: null,
  potenciaMax: null,
};

export default function useFiltrosUsinas(): UseFiltrosUsinasReturn {
  const [busca, setBusca] = useState('');
  const [ufs, setUfs] = useState<string[]>([]);
  const [subsistemasSm, setSubsistemasSm] = useState<string[]>([]);
  const [conjuntos, setConjuntos] = useState<string[]>([]);
  const [potenciaMin, setPotenciaMin] = useState<number | null>(null);
  const [potenciaMax, setPotenciaMax] = useState<number | null>(null);

  // `useDeferredValue` aplica apenas a busca textual — os demais filtros
  // sao multi-select por clique, sem benefício de defer (ja sao raros).
  const buscaDeferida = useDeferredValue(busca);

  const filtros: FiltrosUsinas = useMemo(
    () => ({
      busca, ufs, subsistemasSm, conjuntos, potenciaMin, potenciaMax,
    }),
    [busca, ufs, subsistemasSm, conjuntos, potenciaMin, potenciaMax],
  );

  const filtrosEfetivos: FiltrosUsinas = useMemo(
    () => ({
      busca: buscaDeferida,
      ufs,
      subsistemasSm,
      conjuntos,
      potenciaMin,
      potenciaMax,
    }),
    [buscaDeferida, ufs, subsistemasSm, conjuntos, potenciaMin, potenciaMax],
  );

  const temFiltrosAtivos = useMemo(
    () => (
      busca.trim().length > 0
      || ufs.length > 0
      || subsistemasSm.length > 0
      || conjuntos.length > 0
      || potenciaMin !== null
      || potenciaMax !== null
    ),
    [busca, ufs, subsistemasSm, conjuntos, potenciaMin, potenciaMax],
  );

  const limparFiltros = () => {
    setBusca(FILTROS_VAZIOS.busca);
    setUfs(FILTROS_VAZIOS.ufs);
    setSubsistemasSm(FILTROS_VAZIOS.subsistemasSm);
    setConjuntos(FILTROS_VAZIOS.conjuntos);
    setPotenciaMin(FILTROS_VAZIOS.potenciaMin);
    setPotenciaMax(FILTROS_VAZIOS.potenciaMax);
  };

  return {
    filtros,
    filtrosEfetivos,
    setBusca,
    setUfs,
    setSubsistemasSm,
    setConjuntos,
    setPotenciaMin,
    setPotenciaMax,
    limparFiltros,
    temFiltrosAtivos,
  };
}
